import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import {
  collectionsUrl,
  discoverUrl,
  homeUrl,
  loginUrl,
  myPageUrl,
  signUpUrl,
} from "../../util/url";
import FruitsFamilyLogo from "../icons/FruitsFamilyLogo";
import SearchBar from "../searchBar/SearchBar";
import { useAuthStore } from "../../store/useAuthStore";
import { removeAfterLoginPath } from "../../util/util";
import NavigationSubMenu from "./NavigationSubMenu";
import SellNavigationMenu from "./SellNavigationMenu";
import { useAppInstallModal } from "../../store/useAppInstallModal";

import "./Navigation.css";

export default function Navigation({ hostname, userAgent }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLogin, redirectTo } = useAuthStore();
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const { openModal } = useAppInstallModal();

  useEffect(() => {
    if (redirectTo) {
      navigate(redirectTo);
    }
  }, [redirectTo]);

  //login 페이지 일때를 제외하고는 afterLoginPath 를 제거
  useEffect(() => {
    if (location.pathname !== "/login") {
      removeAfterLoginPath();
    }
  }, [location]);

  const handleMobileDrawerToggle = () => {
    setIsMobileDrawerOpen(!isMobileDrawerOpen);
  };

  const handleMobileDrawerClose = () => {
    setIsMobileDrawerOpen(false);
  };

  const handleDownloadAppClick = (e) => {
    e.preventDefault();
    openModal();
  };

  return (
    <header className="Navigation">
      <nav className="Navigation-nav">
        <div className="Navigation-nav-inner">
          <NavLink className="Navigation-logo" to={homeUrl()}>
            <FruitsFamilyLogo className="Navigation-logo-svg" />
          </NavLink>

          <SearchBar hostname={hostname} userAgent={userAgent}/>

          <ul className="Navigation-links">
            <li className="Navigation-links-item desktop-only">
              <NavLink to={discoverUrl()}>마켓</NavLink>
            </li>
            <li className="Navigation-links-item desktop-only">
              <SellNavigationMenu userAgent={userAgent} />
            </li>
            {!isLogin && (
              <>
                <li className="Navigation-links-item tablet-hide">
                  <NavLink to={signUpUrl()}>회원 가입</NavLink>
                </li>
                <li className="Navigation-links-item tablet-hide">
                  <NavLink to={loginUrl()}>로그인</NavLink>
                </li>
              </>
            )}
            {isLogin && (
              <li className="Navigation-links-item tablet-hide">
                <NavLink to={myPageUrl()}>마이페이지</NavLink>
              </li>
            )}
            <li className="Navigation-links-item">
              <a
                href="#"
                className="Navigation-cta"
                onClick={handleDownloadAppClick}
              >
                Download App
              </a>
            </li>
          </ul>

          <button
            className="Navigation-hamburger"
            onClick={handleMobileDrawerToggle}
            aria-label="메뉴"
          >
            <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile Navigation Drawer */}
      <div
        className={`Navigation-mobile-overlay ${isMobileDrawerOpen ? "active" : ""}`}
        onClick={handleMobileDrawerClose}
      />
      <div
        className={`Navigation-mobile-drawer ${isMobileDrawerOpen ? "active" : ""}`}
      >
        <div className="Navigation-mobile-drawer-header">
          <span className="Navigation-mobile-drawer-title">메뉴</span>
          <button
            className="Navigation-mobile-drawer-close"
            onClick={handleMobileDrawerClose}
            aria-label="메뉴 닫기"
          >
            <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="Navigation-mobile-drawer-body">
          <div className="Navigation-mobile-drawer-section">
            <div className="Navigation-mobile-drawer-section-title">
              카테고리
            </div>
            <NavLink
              to="/vintage-shops"
              className="Navigation-mobile-drawer-link"
              onClick={handleMobileDrawerClose}
            >
              빈티지 샵
            </NavLink>
            <NavLink
              to={collectionsUrl()}
              className="Navigation-mobile-drawer-link"
              onClick={handleMobileDrawerClose}
            >
              기획전
            </NavLink>
            <NavLink
              to="/brand-ranking"
              className="Navigation-mobile-drawer-link"
              onClick={handleMobileDrawerClose}
            >
              브랜드 랭킹
            </NavLink>
          </div>
          <div className="Navigation-mobile-drawer-section">
            <div className="Navigation-mobile-drawer-section-title">
              둘러보기
            </div>
            <NavLink
              to={discoverUrl()}
              className="Navigation-mobile-drawer-link"
              onClick={handleMobileDrawerClose}
            >
              마켓
            </NavLink>
            <SellNavigationMenu
              userAgent={userAgent}
              isMobileDrawer={true}
              onClose={handleMobileDrawerClose}
            />
          </div>
          <div className="Navigation-mobile-drawer-section">
            <div className="Navigation-mobile-drawer-section-title">계정</div>
            {isLogin ? (
              <NavLink
                to={myPageUrl()}
                className="Navigation-mobile-drawer-link"
                onClick={handleMobileDrawerClose}
              >
                마이페이지
              </NavLink>
            ) : (
              <>
                <NavLink
                  to={loginUrl()}
                  className="Navigation-mobile-drawer-link"
                  onClick={handleMobileDrawerClose}
                >
                  로그인
                </NavLink>
                <NavLink
                  to={signUpUrl()}
                  className="Navigation-mobile-drawer-link"
                  onClick={handleMobileDrawerClose}
                >
                  회원 가입
                </NavLink>
              </>
            )}
          </div>
        </div>
        <a
          href="#"
          className="Navigation-mobile-drawer-cta"
          onClick={(e) => {
            handleDownloadAppClick(e, "모바일 드로어 Download App 버튼");
            handleMobileDrawerClose();
          }}
        >
          Download App
        </a>
      </div>

      <NavigationSubMenu />
    </header>
  );
}
Navigation.propTypes = {
  hostname: PropTypes.string.isRequired,
  userAgent: PropTypes.string.isRequired,
};
